import { useState, useEffect, useCallback } from 'react';
import { useProperty } from '../contexts/PropertyContext';
import { getHomeAssets, Asset } from '../services/asset.api';
import { getMaintenanceSchedule, ScheduledTask } from '../services/maintenance.api';
import { getIssues, Issue } from '../services/issues.api';
import { getNotifications, AppNotification } from '../services/notifications.api';
import { getUserBookings, Booking } from '../services/booking.api';
import { getBusinessRules, type BusinessRules } from '../services/businessRules.api';

// ─── HELPERS ────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  hvac: 'HVAC Systems',
  heating: 'HVAC Systems',
  cooling: 'HVAC Systems',
  kitchen: 'Kitchen Appliances',
  appliance: 'Appliances',
  laundry: 'Laundry',
  smart: 'Smart Devices',
  security: 'Security',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  outdoor: 'Outdoor Equipment',
  entertainment: 'Entertainment',
  cleaning: 'Cleaning',
};

function getCategoryLabel(raw: string): string {
  const lower = raw.toLowerCase();
  for (const key of Object.keys(CATEGORY_LABELS)) {
    if (lower.includes(key)) return CATEGORY_LABELS[key];
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function getHealthStatus(score: number): 'excellent' | 'good' | 'attention' {
  if (score >= 85) return 'excellent';
  if (score >= 65) return 'good';
  return 'attention';
}

function getTaskPriority(nextDueAt: string): 'high' | 'medium' | 'low' {
  const daysUntilDue = Math.ceil(
    (new Date(nextDueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  if (daysUntilDue <= 1) return 'high';
  if (daysUntilDue <= 7) return 'medium';
  return 'low';
}

export function formatDueDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'Overdue';
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff <= 7) return `In ${diff} days`;
  if (diff <= 30) return `In ${Math.ceil(diff / 7)} week${diff > 14 ? 's' : ''}`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatBookingDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface AssetCategory {
  category: string;
  count: number;
  avgHealth: number;
  status: 'excellent' | 'good' | 'attention';
  icon?: string;
}

export interface UpcomingTask {
  id: string;
  title: string;
  assetName?: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  isOverdue: boolean;
}

export interface DashboardData {
  // Raw data
  assets: Asset[];
  /** Smart-schedule tasks (same source the Maintenance page + mobile app use). */
  maintenanceRecords: ScheduledTask[];
  issues: Issue[];
  bookings: Booking[];
  notifications: AppNotification[];

  // Stats
  totalAssets: number;
  avgHealthScore: number;
  /** Maintenance tasks with nextDueAt within next 7 days */
  dueMaintenance: number;
  /** Open + in_progress issues */
  openIssues: number;

  // Derived sections
  assetsByCategory: AssetCategory[];
  upcomingTasks: UpcomingTask[];
  upcomingBookings: Booking[];
  recentNotifications: AppNotification[];

  // State
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

// ─── HOOK ────────────────────────────────────────────────────────────────────

export function useDashboardData(): DashboardData {
  const { currentProperty } = useProperty();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [maintenanceRecords, setMaintenance] = useState<ScheduledTask[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  // Business rules from backend (replaces hardcoded thresholds: 70 for low
  // health, 30/90 days for warranty expiring soon).
  const [rules, setRules] = useState<BusinessRules | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!currentProperty?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const homeId = currentProperty.id;

    Promise.allSettled([
      getHomeAssets(homeId),
      getMaintenanceSchedule(homeId),
      getIssues(homeId),
      getUserBookings(),
      getNotifications(),
      getBusinessRules(),
    ]).then(([assetsR, maintenanceR, issuesR, bookingsR, notificationsR, rulesR]) => {
      if (assetsR.status === 'fulfilled') setAssets(assetsR.value);
      else console.error('[Dashboard] Failed to load assets:', assetsR.reason);

      if (maintenanceR.status === 'fulfilled') setMaintenance(maintenanceR.value);
      else console.error('[Dashboard] Failed to load maintenance:', maintenanceR.reason);

      if (issuesR.status === 'fulfilled') setIssues(issuesR.value);
      else console.error('[Dashboard] Failed to load issues:', issuesR.reason);

      if (bookingsR.status === 'fulfilled') setBookings(bookingsR.value);
      else console.error('[Dashboard] Failed to load bookings:', bookingsR.reason);

      if (notificationsR.status === 'fulfilled') setNotifications(notificationsR.value);
      else console.error('[Dashboard] Failed to load notifications:', notificationsR.reason);

      if (rulesR.status === 'fulfilled') setRules(rulesR.value);
      else console.error('[Dashboard] Failed to load business rules:', rulesR.reason);

      setIsLoading(false);
    });
  }, [currentProperty?.id, refreshKey]);

  // ── Computed stats ────────────────────────────────────────────────────────

  const totalAssets = assets.length;

  const avgHealthScore =
    assets.length > 0
      ? Math.round(assets.reduce((sum, a) => sum + (a.healthScore ?? 80), 0) / assets.length)
      : 0;

  const now = new Date();
  const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  // Warranty "expiring soon" window now comes from tenant settings (was 30 days hardcoded).
  const warrantyExpiringDays = rules?.warrantyExpiringDays ?? 30;
  const _lowHealthThreshold = rules?.lowHealthThreshold ?? 70;
  const _inWarrantyWindow = new Date(now.getTime() + warrantyExpiringDays * 24 * 60 * 60 * 1000);

  // Same logic the Maintenance page uses: count smart-schedule tasks due
  // within the next 7 days that are still actionable.
  const dueMaintenance = maintenanceRecords.filter((r) => {
    if (!r.dueDate) return false;
    const due = new Date(r.dueDate);
    return due <= in7Days && r.status !== 'completed' && r.status !== 'skipped';
  }).length;

  const openIssues = issues.filter(
    (i) => i.status === 'open' || i.status === 'in_progress'
  ).length;

  // ── Asset health by category ──────────────────────────────────────────────

  const categoryMap = new Map<string, { healthSum: number; count: number }>();
  for (const asset of assets) {
    const key = getCategoryLabel(asset.category);
    const existing = categoryMap.get(key) ?? { healthSum: 0, count: 0 };
    categoryMap.set(key, {
      healthSum: existing.healthSum + (asset.healthScore ?? 80),
      count: existing.count + 1,
    });
  }

  const assetsByCategory: AssetCategory[] = Array.from(categoryMap.entries())
    .map(([category, { count, healthSum }]) => {
      const avgHealth = Math.round(healthSum / count);
      return { category, count, avgHealth, status: getHealthStatus(avgHealth) };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // ── Upcoming maintenance tasks ────────────────────────────────────────────

  const upcomingTasks: UpcomingTask[] = maintenanceRecords
    .filter((r) => r.dueDate && r.status !== 'completed' && r.status !== 'skipped')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .slice(0, 5)
    .map((r) => ({
      id: r.id,
      title: r.title,
      assetName: r.assetName,
      dueDate: formatDueDate(r.dueDate),
      priority: getTaskPriority(r.dueDate),
      isOverdue: new Date(r.dueDate) < now,
    }));

  // ── Smart insights ──────────────────────────────────────────────────────
  // (Removed — insights feature deprecated.)

  // ── Upcoming bookings ─────────────────────────────────────────────────────

  const upcomingBookings = bookings
    .filter((b) => {
      const sDate = new Date(b.scheduledDate);
      return (
        sDate >= now &&
        ['scheduled', 'confirmed', 'pending'].includes(b.status.toLowerCase())
      );
    })
    .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
    .slice(0, 3);

  // ── Recent notifications ──────────────────────────────────────────────────

  const recentNotifications = notifications.slice(0, 6);

  return {
    assets,
    maintenanceRecords,
    issues,
    bookings,
    notifications,
    totalAssets,
    avgHealthScore,
    dueMaintenance,
    openIssues,
    assetsByCategory,
    upcomingTasks,
    upcomingBookings,
    recentNotifications,
    isLoading,
    error,
    refresh,
  };
}
